/**
 * EvalLedgerDrawer.tsx  — Slice 1e
 *
 * A default-CLOSED drawer for the Drill-In Ledger.
 *
 * Differs from the platform's generic CollapsibleSection in two ways:
 *  1. Defaults to CLOSED (not open) — every ledger drawer starts collapsed.
 *  2. Accepts a `summary` prop: a one-line string rendered inside the header
 *     (to the right of the title) so the user can read key data without
 *     opening the drawer.
 *
 * Styling:
 *  All surface / text / radius decisions consume `--results-*` tokens, not raw
 *  hex or platform `--color-*` tokens, consistent with RESULTS_THEME_CONVENTION.md.
 *
 * Usage:
 *  <EvalLedgerDrawer
 *    title="Goal checks"
 *    summary="5 of 6 met"
 *    guideSection="goal-achievements"     // ← data-guide-section carried onto wrapper
 *    data-testid="ledger-drawer-goals"
 *  >
 *    <GoalResultsSection ... />
 *  </EvalLedgerDrawer>
 */

import { useState, useId, type ReactNode, type ComponentType } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/cn';

export interface EvalLedgerDrawerProps {
  /** Drawer title shown in the collapsed header button. */
  title: string;
  /**
   * One-line summary rendered next to the title.
   * Visible even when the drawer is closed — the key data the user needs at a glance.
   * Example: "5 of 6 met", "Prep 92 · Exec 78 · Recovery 64", "no wasted motion".
   */
  summary?: string;
  /** Optional icon displayed before the title. */
  icon?: ComponentType<{ className?: string }>;
  /**
   * data-guide-section value carried onto the outer wrapper div.
   * The scroll-spy (`useSectionSpy`) uses this attribute to detect which section
   * is in view. Pass the same ID that was on the previous flat-stack wrapper.
   */
  guideSection?: string;
  /** Whether the drawer is open by default. Must be false for ledger drawers (default). */
  defaultOpen?: boolean;
  /** Content revealed when the drawer is open. */
  children: ReactNode;
  /** Extra class on the outer wrapper div. */
  className?: string;
  /** data-testid forwarded to the outer wrapper div. */
  'data-testid'?: string;
}

/**
 * EvalLedgerDrawer — one collapsible drawer in the Drill-In Ledger.
 * Default closed, summary line visible in header, --results-* tokens throughout.
 */
function EvalLedgerDrawer({
  title,
  summary,
  icon: Icon,
  guideSection,
  defaultOpen = false,
  children,
  className,
  'data-testid': testId,
}: EvalLedgerDrawerProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const contentId = useId();

  return (
    <div
      className={cn('overflow-hidden', className)}
      style={{
        borderRadius: 'var(--results-radius-card)',
        border: '1px solid var(--results-surface-line)',
        background: 'var(--results-surface-raised-1)',
      }}
      data-guide-section={guideSection}
      data-testid={testId}
    >
      {/* Header / toggle button */}
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        aria-controls={contentId}
        className={cn(
          'w-full flex items-center gap-3 px-4 py-3.5',
          'text-left',
          'transition-colors duration-150',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-inset',
        )}
        style={{
          color: 'var(--results-text-primary)',
          // Subtle hover tint without breaking the token contract
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background =
            'var(--results-surface-raised-2)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        }}
      >
        {/* Left cluster: icon + title + summary */}
        <div className="flex items-center gap-2.5 min-w-0 flex-1">
          {Icon && (
            <Icon className="h-4 w-4 flex-shrink-0 text-[var(--results-text-dim)]" />
          )}
          <span
            className="text-sm font-semibold truncate"
            style={{ color: 'var(--results-text-primary)', fontFamily: 'var(--font-results-body)' }}
          >
            {title}
          </span>
          {summary && (
            <>
              <span
                className="flex-shrink-0 hidden sm:inline"
                style={{ color: 'var(--results-surface-line)', fontSize: '12px' }}
                aria-hidden="true"
              >
                —
              </span>
              <span
                className="text-xs truncate hidden sm:inline"
                style={{
                  color: 'var(--results-text-dim)',
                  fontFamily: 'var(--font-results-mono)',
                }}
                data-testid={testId ? `${testId}-summary` : undefined}
                aria-label={`Summary: ${summary}`}
              >
                {summary}
              </span>
            </>
          )}
        </div>

        {/* Summary on mobile (below title row is impractical; show a compact badge) */}
        {summary && (
          <span
            className="text-xs flex-shrink-0 sm:hidden"
            style={{
              color: 'var(--results-text-dim)',
              fontFamily: 'var(--font-results-mono)',
            }}
            aria-hidden="true"
          >
            {summary}
          </span>
        )}

        {/* Chevron */}
        <ChevronDownIcon
          className={cn(
            'h-4 w-4 flex-shrink-0 transition-transform duration-200 ease-out',
            'text-[var(--results-text-faint)]',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      {/* Expandable content — CSS grid trick for smooth open/close */}
      <div
        id={contentId}
        role="region"
        aria-labelledby={contentId}
        className={cn(
          'grid transition-[grid-template-rows] duration-200 ease-out',
          isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
        )}
      >
        <div className="overflow-hidden">
          <div
            className="px-4 pb-4 pt-2"
            style={{ borderTop: '1px solid var(--results-surface-line)' }}
          >
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EvalLedgerDrawer;
